<template>
  <div class="tw-flex tw-gap-4">
    <div class="whitebackground tw-bg-white tw-px-12 tw-pt-12 tw-pb-7 tw-max-w-5xl">
      <b-notification
        v-if="justSubmitted"
        type="is-success"
      >
        Successfully submitted portfolio to First Affirmative for review
      </b-notification>
      <b-notification
        v-if="checkupStore.optimizationAdditionalWarnings?.warnings?.length > 0 && !isUploading"
        type="is-warning"
      >
        <div class="tw-flex tw-items-center tw-gap-5">
          <i class="fa fa-exclamation-triangle tw-text-2xl" />
          <div class="tw-w-full">
            <div
              v-for="(warning, index) in checkupStore.optimizationAdditionalWarnings?.warnings"
              :key="index"
            >
              <p class="tw-font-bold class tw-mb-3">
                - {{ warning }}
              </p>
            </div>
            <div v-if="checkupStore.optimizationAdditionalWarnings?.suggestion">
              <p class="tw-font-bold tw-mb-3">
                {{ checkupStore.optimizationAdditionalWarnings?.suggestion }}
              </p>
              <p
                v-if="checkupStore.optimizationAdditionalWarnings?.metricNames?.length > 0"
                class="tw-mb-3"
              >
                Metrics:
                <span
                  v-for="(metric, index) in checkupStore.optimizationAdditionalWarnings?.metricNames"
                  :key="index"
                >
                  {{ metric }}<span v-if="index < checkupStore.optimizationAdditionalWarnings?.metricNames.length - 1">, </span>
                </span>
              </p>
            </div>
            <button
              v-if="checkupStore.personEntity?.external_id"
              class="button"
              @click="handleValueWarningClick"
            >
              Go to workflow in order to change values
            </button>
          </div>
        </div>
      </b-notification>
      <b-notification
        v-if="checkupStore.optimizationError"
        type="is-danger"
      >
        <div class="tw-flex tw-items-center tw-gap-5">
          <i class="fa fa-times-circle tw-text-2xl" />
          <div class="tw-w-full">
            <p class="tw-font-bold tw-mb-3">
              Optimization Failed
            </p>
            <p
              v-if="showDetailedError"
              class="tw-mb-3 tw-whitespace-pre-line"
            >
              {{ optimizationErrorMessage }}
            </p>
            <p
              v-else
              class="tw-mb-3"
            >
              The optimization could not be completed with the current constraints. Please try adjusting your portfolio preferences and try again.
            </p>
            <button
              class="button"
              @click="checkupStore.optimizationError = null"
            >
              Dismiss
            </button>
          </div>
        </div>
      </b-notification>
      <div class="columns">
        <div class="column is-two-thirds">
          <p class="title is-1">
            Portfolio Optimization
          </p>
          <p class="subtitle">
            Methodology & Results
          </p>
          <div
            v-for="(o, index) in checkupStore?.optimization.optimizations"
            :key="index"
          >
            <!-- Combined portfolio download (always shown) -->
            <div
              class="tw-bg-[#7959d5] tw-mb-2 tw-text-xs tw-px-3 tw-py-1.5 tw-cursor-pointer tw-rounded-sm"
            >
              <a
                class="tw-text-white hover:tw-text-white"
                :href="'/reportholdingscsv/' + (o?.optimization_portfolio_slug || o?.optimization_portfolio?.slug)"
                target="_blank"
              >
                Download Optimized Portfolio Holdings for
                {{ o.optimization_portfolio_name }}
              </a>
            </div>
            <!-- Per-account portfolio downloads (additionally shown when tax lot accounts present) -->
            <template v-if="o.account_portfolios && o.account_portfolios.length > 0 && checkupStore.featureFlags.includes('Feature: Portfolio Checkup Optimization Tax')">
              <div
                v-for="(acctPort, apIdx) in o.account_portfolios"
                :key="apIdx"
                class="tw-bg-[#7959d5] tw-mb-2 tw-text-xs tw-px-3 tw-py-1.5 tw-cursor-pointer tw-rounded-sm"
              >
                <a
                  class="tw-text-white hover:tw-text-white"
                  :href="'/reportholdingscsv/' + acctPort.slug"
                  target="_blank"
                >
                  Download Optimized Portfolio Holdings for {{ acctPort.name }}{{ acctPort.account_type_name ? ` (${acctPort.account_type_name})` : '' }}
                </a>
              </div>
            </template>
            <button
              v-if="o?.diagnostics?.tax_lot_trades && Object.keys(o.diagnostics.tax_lot_trades).length && checkupStore.featureFlags.includes('Feature: Portfolio Checkup Optimization Tax')"
              class="button is-small is-primary tw-mb-2"
              @click="downloadTaxLotTrades(index)"
            >
              Download Tax Lot Trades for {{ o.optimization_portfolio_name }}
            </button>
            <!-- Asset Location Recommendations -->
            <div
              v-if="o.asset_location_recommendations && o.asset_location_recommendations.length > 0"
              class="tw-mt-4"
            >
              <p class="tw-font-medium tw-mb-2">Asset Location Recommendations</p>
              <table class="table is-striped is-fullwidth tw-text-sm">
                <thead>
                  <tr>
                    <th>Security</th>
                    <th>Recommended Account Type</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(rec, rIdx) in o.asset_location_recommendations"
                    :key="rIdx"
                  >
                    <td>{{ rec.ticker }}</td>
                    <td>{{ rec.account_tax_status }}</td>
                    <td>${{ rec.dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <button
            v-if="advisorStore.advisor?.firm?.name == 'First Affirmative'"
            class="button is-small is-primary"
            @click="downloadBuyListAsCSV"
          >
            Download Buy List as CSV
          </button>

          <b-button
            v-if="advisorStore.advisor?.firm?.parent_firm?.name === 'First Affirmative' && checkupStore.personEntity?.external_id"
            label="Submit Portfolio(s) to First Affirmative"
            type="is-link"
            icon-pack="fas"
            icon-right="arrow-right"
            @click="submitToFAFN"
          />

          <template v-if="advisorStore.advisor.email === 'kaitlynmitchell@firstaffirmative.com'">
            <button
              v-if="!accessGiven"
              :disabled="!checkupStore.optimizedPortfolio"
              class="button is-small is-primary"
              @click="giveAccessToPersonEntityAdvisor"
            >
              Give Access to Client's Advisor
            </button>
            <p
              v-else
              class="has-text-success"
            >
              Access given to Client's Advisor.
            </p>
          </template>
        </div>
        <!-- If current user is kaitlyn, then button to give access to personEntity.advisor (mp.advisor.add) -->
        <div class="column is-one-third">
          <div class="moneysaved has-text-centered">
            <div class="tw-flex tw-flex-col tw-text-xs tw-uppercase tw-tracking-wider tw-text-gray-500">
              <p>
                Tracking Error vs
              </p>
              <p class="tw-flex tw-items-center tw-gap-2">
                <span>
                  {{ checkupStore.riskBasedBenchmark?.nickname || 'Benchmark' }}
                </span>
                <StakeTooltip label="Tracking error compares the optimized portfolio to a benchmark based on your specified risk level">
                  <i class="fa fa-info-circle tw-text-base tw-text-[#F1583D]" />
                </StakeTooltip>
              </p>
            </div>

            <div
              v-for="(optimization, index) in checkupStore.optimization.optimizations"
              :key="index"
            >
              <p class="title is-2 is-family-code">
                {{ (optimization.diagnostics.tracking_error * 100).toFixed(2) }}%
              </p>
            </div>
          </div>
          <br>
          <div class="help has-text-grey">
            <p>YourStake's optimizer calculates the tracking error as the square root of the sum of the squares of the difference in daily returns between the optimized portfolio and the original portfolio based on a proprietary YourStake factor model.</p>
          </div>


        <!-- <button
                    v-if="checkupStore?.optimization"
                    class="button"
                    @click="checkupStore.sendToOrion"
                  >
                    Send to Orion
                  </button> -->
        </div>
      </div>

      <br>
      <div class="container">
        <div class="content has-text-grey">
          <p class="is-size-4 has-text-weight-semibold">
            <span class="has-text-primary ">
              Congratulations,
            </span>
            YourStake's portfolio optimizer has constructed a new direct index portfolio that
            <span class="has-text-primary has-text-weight-semibold">
              is tailored uniquely to you.
            </span>
          </p>
          <p>
            This portfolio was created through a robust quantitative process incorporating your risk and values preferences. Your optimal portfolio is designed to minimize tracking error versus the risk-appropriate allocation benchmark for you.
          </p>
          <h4>
            Executive Summary
          </h4>
          <p>
            Securities for the optimized portfolio are chosen based on their availability for fractional share trading, that have traded recently, have a minimum market cap of $150 million to ensure liquidity, and alignment with the values selected using YourStake's metrics.
            The individual stocks comprising the equity portion of the portfolio are selected based on the optimization, while the fixed income allocation consists of a selection of mutual funds and ETFs.
          </p>

          <p>Benchmarks have different allocations of fixed-income, cash, and equities based on the risk preferences selected. Risk and return data is based on past performance, which is not indicative of future results. </p>
        </div>
        <div class="content has-text-grey">
          <hr>
          <h1> How this Optimization Works</h1>
          <h3>Understanding the Basics</h3>

          <p><strong>Benchmark Selection:</strong> The foundation of the process is selecting a benchmark based on your risk preferences. This benchmark acts as a reference point, guiding the overall risk and return profile your portfolio aims to mimic. Benchmarks consist of different weights of ETFs designed to track the standard MSCI All Country World Index (ACWI), the Bloomberg Aggregate Bond Index (AGG), and cash depending on your risk tolerance.</p>

          <p><strong>Values Preferences:</strong> Your investment decisions are influenced by the specific values you wish to support or avoid. Unlike traditional portfolios that may solely focus on financial metrics, this approach prioritizes aligning investments with personal or ethical values drawn from a broad spectrum of values preferences.</p>

          <h3 class="title is-3">
            The Four-Step Optimization Process
          </h3>

          <ol type="1">
            <li>
              <strong>Universe Screening Based on Values Preferences</strong>
              <ul>
                <li><strong>Inclusions and Exclusions:</strong> The universe of available securities is screened based on your values preferences. This involves excluding securities that don't meet your criteria and endeavoring to include those that do. Specific-company inclusions and exclusions can be applied for specific securities. Category-style inclusions can be applied to increase the portfolio alignment with specific values.</li>
              </ul>
            </li>
            <li>
              <strong>Market Cap Alignment Optimization</strong>
              <ul>
                <li><strong>Feasibility Analysis:</strong> The first step assesses how feasible it is to align the market caps of securities in the portfolio with those in the benchmark, given your values preferences and available securities.</li>
                <li><strong>Constraint Setting:</strong> Based on this feasibility analysis, we apply a constraint ensuring that the portfolio's market cap distribution is within 1% of the closest feasible alignment to the benchmark. This step ensures that the portfolio maintains a similar risk/return profile to the benchmark within the confines of available, values-aligned securities.</li>
              </ul>
            </li>
            <li>
              <strong>Factor Model Optimization with Tracking Error Adjustment</strong>
              <ul>
                <li><strong>Initial Constraints:</strong> Initial constraints are applied to refine the selection process, ensuring a diversified and manageable portfolio. This includes limiting equity securities to no more than 2% of their weight in the benchmark, requiring US equity exposure to be at least as much as the benchmark, and international equity exposure to be no more than the benchmark.</li>
                <li><strong>Optimization Goals:</strong> Using a factor model, the portfolio is further optimized within the initial constraints, aiming for minimum tracking error to the benchmark.</li>
                <li><strong>Tracking Error Management:</strong> The tracking error, or the deviation of the portfolio's returns from the benchmark, is carefully managed. If the initial optimization results in a tracking error less than 2%, that becomes the maximum allowed. If above, the maximum allowed is set to 0.25% above this error, balancing portfolio performance with benchmark alignment. The tracking error is calculated as the standard deviation of daily returns times the square root 252 to annualize it.</li>
              </ul>
            </li>
            <li>
              <strong>Maximizing Values Alignment and Minimizing Tracking Error</strong>
              <ul>
                <li><strong>Values:</strong> The portfolio is optimized to increase the weight of inclusion categories, helping maximize alignment with your values within the tracking error constraints.</li>
                <li><strong>Final Adjustments:</strong> The last step involves fine-tuning the values alignment of the optimized portfolio compared to the benchmark to ensure they are close to the optimal inclusion values, with a goal to minimize the tracking error, ensuring close tracking of the benchmark while upholding your values preferences. Note that subsequent optimizations with the same preferences will be different from this optimization due to market fluctuations.</li>
              </ul>
            </li>
          </ol>

          <h3 class="title is-3">
            Conclusion
          </h3>

          <p>This structured four-step process is designed to construct a portfolio that aims to enable you to meet your financial objectives in relation to a chosen benchmark. The portfolio is also constructed to be aligned with your personal values. By starting with a critical assessment of market cap and geographic alignment and incorporating strategic securities selection, the approach ensures a structurally sound portfolio. It blends values-based investing with rigorous financial optimization, aiming to achieve the best of both worlds: supporting values you care about while seeking to meet your financial goals.</p>
        </div>
      </div>
    </div>

    <!-- Settings Sidebar -->
    <AreaSidebar optimizer>
      <!--
     This should instruct the user on why we are doing the optimization, what they can do with it, and how they can contact first affirmative to have this be managed on their behalf.
    -->


      <div class="sidebar-container">
        <div class="sidebar">
          <h4 class="title is-4 has-text-weight-bold">
            Your Personalized Portfolio Optimization
          </h4>
          <div class="content">
            <h5 class="title is-5">
              About This Optimization
            </h5>
            <ul>
              <li>YourStake has created an optimized portfolio based on the risk and (optionally) values preferences you selected</li>
              <li>Review the methodology and calculations on the left for a detailed understanding of our optimization process</li>
              <li>This is an informational tool, and does not constitute investment advice or a recommendation to purchase any securities</li>
              <li>You can view and download the portfolio holdings for your reference</li>
              <li>You may use the downloaded holdings as you see fit for no additional cost</li>
              <li>To hide this optimized portfolio, uncheck it in the top left</li>
            </ul>

            <h5 class="title is-5">
              Implementation Options
            </h5>
            <p>If you're interested in implementing this portfolio:</p>
            <ul>
              <li>First Affirmative Financial Network (First Affirmative) can manage this portfolio for you as a Separately Managed Account (SMA).</li>
              <li>First Affirmative handles trading, rebalancing, acts as a fiduciary, and can customize portfolios for tax management</li>
              <li>First Affirmative supports multiple custodians for portfolio management.</li>
            </ul>

            <h5 class="title is-5">
              Next Steps
            </h5>
            <p>Ready to explore further?</p>
            <div class="has-text-centered">
              <a
                href="https://www.yourstake.org/product-tours/personalized-direct-indexing"
                class="button is-primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn More About First Affirmative
              </a>
            </div>
            <p class="mt-3">
              Click the button above to:
            </p>
            <ul>
              <li>Access materials about First Affirmative</li>
              <li>Get answers to compliance questions</li>
              <li>Schedule a meeting with First Affirmative's Chief Investment Officer</li>
              <li>Learn about necessary documentation to proceed</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- <IncludeInPresentationToggle :name="area?.name" /> -->
      <!-- <SpeakersNotes :notes="area?.explanation" /> -->
    </AreaSidebar>
  </div>
</template>


<script>
import { mapStores } from 'pinia';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';

import StakeTooltip from '@stakeui/StakeTooltip.vue';
import AreaSidebar from '@frontend/ui/features/checkup/components/sidebar/AreaSidebar.vue';


export default {
  name: 'OptimizerArea',
  components: {
    StakeTooltip,
    AreaSidebar,
  },
  data() {
    return {
      accessGiven: false,
      justSubmitted: false,
    };
  },
  computed: { /*
    Creates a new property
    Updates when any dependant property changes */
    ...mapStores(useCheckupStore, useAdvisorStore),
    showDetailedError() {
      // Show detailed error if the "Feature: Portfolio Checkup Optimization Tax" feature flag is enabled
      return this.checkupStore.featureFlags.includes('Feature: Portfolio Checkup Optimization Tax');
    },
    optimizationErrorMessage() {
      if (!this.checkupStore.optimizationError) return '';

      const error = this.checkupStore.optimizationError;
      const errorMsg = error?.task?.meta?.error_message || '';

      // If there are violated constraints, format them nicely
      if (error?.task?.meta?.diagnostics?.violated_constraints?.length > 0) {
        const constraints = error.task.meta.diagnostics.violated_constraints;

        // Make constraint names more readable
        const readableConstraints = constraints.map(c => {
          return c
              .replace(/constraint_/g, '')
              .replace(/_/g, ' ')
              .replace(/\b\w/g, l => l.toUpperCase());
        });

        return `Optimization failed due to conflicting constraints:\n\n${readableConstraints.map(c => `• ${c}`).join('\n')}\n\n${errorMsg}`;
      }

      return errorMsg || 'An unknown error occurred during optimization.';
    },
  },
  methods: {
    handleValueWarningClick() {
      const personEntityID = this.checkupStore.personEntity?.linked_entity_external_id;
      const url = `/advisor-dashboard/direct-indexing/${personEntityID}?step=values`;
      window.open(url, '_blank');
    },
    async submitToFAFN() {
      const opts = this.checkupStore?.optimization.optimizations;

      const portfolioIds = opts?.map(opt => opt.optimization_portfolio_id) || [];
      const portfolioNicknames = opts?.map(opt => opt.optimization_portfolio_name) || [];

      const request = await fetch(`/api/v1/integrations/fafn/account_dndx/${this.checkupStore.personEntity.linked_entity_external_id}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': this.$root.$data.csrfToken,
        },
        body: JSON.stringify({
          fafn_dndx: {
            optPortfolioIds: portfolioIds,
            optPortfolioNicknames: portfolioNicknames,
            enabled: true,
          },
          notify: true,
        }),
      });
      await request.json();
      this.justSubmitted = true;
    },
    downloadTaxLotTrades(idx) {
      let taxLotTrades = this.checkupStore.optimization.optimizations[idx].diagnostics.tax_lot_trades;
      const buyList = this.checkupStore.optimization.optimizations[idx].buy_list || this.checkupStore.optimization.optimizations[idx].diagnostics?.buy_list;
      const buyListTickers = buyList?.map(item => item.ticker__symbol) || [];
      let csvContent = 'data:text/csv;charset=utf-8,';

      // Detect old flat format { column: { row: value } } vs account-wrapped { accountName: { column: { row: value } } }
      // Old format has exactly the 5 known column names as its top-level keys. Checking for
      // exact equality (not just any-match) prevents false positives if an account happens
      // to share a name with one of the column strings.
      const KNOWN_COLUMNS = ['Security', 'Initial Amount', 'Trade Amount', 'Long Term Capital Gain/Loss', 'Short Term Capital Gain/Loss'];
      const topKeys = Object.keys(taxLotTrades);
      if (topKeys.length === KNOWN_COLUMNS.length && KNOWN_COLUMNS.every(c => topKeys.includes(c))) {
        taxLotTrades = { 'Default': taxLotTrades };
      }

      const csvCell = v => {
        const s = v != null ? String(v) : '';
        return s.includes(',') || s.includes('"') || s.includes('\n') ? '"' + s.replace(/"/g, '""') + '"' : s;
      };

      const firstAccount = Object.keys(taxLotTrades)[0];
      const tradeColumns = Object.keys(taxLotTrades[firstAccount] || {});
      csvContent += ['Account', ...tradeColumns, 'On Buy List'].map(csvCell).join(',') + '\n';
      Object.entries(taxLotTrades).forEach(([accountName, accountTrades]) => {
        const rows = Object.keys(accountTrades[tradeColumns[0]] || {});
        rows.forEach(row => {
          const values = tradeColumns.map(col => csvCell(accountTrades[col]?.[row]));
          const security = accountTrades['Security']?.[row] || '';
          csvContent += [csvCell(accountName), ...values, buyListTickers.includes(security) ? 'Yes' : 'No'].join(',') + '\n';
        });
      });
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', 'tax_lot_trades.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    downloadBuyListAsCSV() {
      const buyList = this.checkupStore.optimization.optimizations[0].buy_list;
      let csvContent = 'data:text/csv;charset=utf-8,';
      // Add CSV header
      csvContent += 'Ticker,Dollars\n';
      // Add rows for each item in the buy list
      buyList.forEach(item => {
        const row = `${item.ticker__symbol},${1}`;
        csvContent += row + '\n';
      });
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', 'buy_list.csv');
      document.body.appendChild(link); // Required for FF

      link.click(); // Trigger the download
      document.body.removeChild(link); // Clean up
    },
    async giveAccessToPersonEntityAdvisor() {
      const opts = this.checkupStore?.optimization.optimizations;

      let portfolioIds = opts?.map(opt => opt.optimization_portfolio_id) || [];
      const equityOnlyPortfolioIds = opts?.map(opt => opt.optimization_equity_only_portfolio_id) || [];

      portfolioIds = [...portfolioIds, ...equityOnlyPortfolioIds];

      const personEntityId = this.checkupStore.personEntity.external_id;
      try {
        for (const portfolioId of portfolioIds) {
          const response = await fetch('/api/v1/checkup/optimization/grant-access/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': this.$root.$data.csrfToken,
            },
            body: JSON.stringify({ portfolioId, personEntityId }),
          });
          if (!response.ok) {
            throw new Error(`Failed to grant access for portfolio ID: ${portfolioId}`);
          }
          await response.json();
        }
        this.accessGiven = true;
      } catch (error) {
        console.error('Error granting access:', error);
      }
    },
  },
};
</script>


  <style scoped>
 @keyframes strong-orange-glow {
    0% {
      box-shadow: 0 0 15px rgba(255, 140, 0, 0.4);
    }
    50% {
      box-shadow: 0 0 30px rgba(255, 140, 0, 0.7);
    }
    100% {
      box-shadow: 0 0 15px rgba(255, 140, 0, 0.4);
    }
  }

  .sidebar-container {
    position: relative;
  }

  :deep(.area-sidebar) {
    animation: strong-orange-glow 6s ease-in-out infinite;
    border-radius: 8px;
    overflow: hidden;
  }

  .sidebar {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 8px;
    transition: all 0.3s ease;
  }

  .sidebar:hover {
    transform: translateY(-5px);
  }
  </style>
